import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { licenseCounters } from '../utils/licenseDisplay';

interface AddLicenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLicenseAdded: (licenseInfo: any) => void;
}

interface LicenseInfo {
  code: string;
  type: string;
  username: string;
  expiryDate: string;
  isValid: boolean;
  hwid?: string;
  vpsIp?: string;
  vpsPort?: string;
  grabber?: boolean;
  mailer?: boolean;
  debouncer?: boolean;
  stripe?: boolean;
  premiumProxy?: boolean;
  totalScans?: number;
  level?: number;
}

const AddLicenseModal: React.FC<AddLicenseModalProps> = ({
  isOpen,
  onClose,
  onLicenseAdded
}) => {
  const [licenseCode, setLicenseCode] = useState('');
  const [hwid, setHwid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Générer un HWID unique pour cet appareil
  React.useEffect(() => {
    const generateHWID = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('HWID_GENERATOR', 2, 2);
        const fingerprint = canvas.toDataURL();
        const hwid = btoa(fingerprint + navigator.userAgent + screen.width + screen.height);
        setHwid(hwid);
      }
    };
    generateHWID();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('https://glitchcloud.app/license-api/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          license: licenseCode,
          hwid: hwid
        })
      });

      const data = await response.json();

      if (data.valid) {
        const licenseInfo: LicenseInfo = {
          code: licenseCode,
          type: data.type,
          username: data.username || `User_${data.type}`,
          expiryDate: data.expiryDate,
          isValid: true,
          hwid: hwid,
          vpsIp: data.vpsIp,
          vpsPort: data.vpsPort,
          grabber: data.grabber || false,
          mailer: data.mailer || false,
          debouncer: data.debouncer || false,
          stripe: data.stripe || false,
          premiumProxy: data.premiumProxy || false,
          ...licenseCounters(data),
        };
        
        // Notifier le parent que la licence a été ajoutée avec succès
        onLicenseAdded(licenseInfo);
        
        // Close le modal
        onClose();
        
      } else {
        setError(data.message || 'Invalid license code');
      }
    } catch (error) {
      setError('Could not reach the license server');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e: React.MouseEvent) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl border border-purple-500/30 max-w-md w-full overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600/20 to-violet-600/20 p-6 border-b border-purple-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-violet-500 rounded-xl flex items-center justify-center">
                  <span className="text-xl">🔑</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Ajouter une Licence</h2>
                  <p className="text-purple-300 text-sm">Vérifiez et ajoutez une nouvelle licence</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="license" className="block text-sm font-medium text-purple-200 mb-3">
                  License Code
                </label>
                <input
                  type="text"
                  id="license"
                  value={licenseCode}
                  onChange={(e) => setLicenseCode(e.target.value)}
                  placeholder="Entrez votre code de licence"
                  className="w-full px-4 py-4 bg-purple-900/30 border border-purple-500/50 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="hwid" className="block text-sm font-medium text-purple-200 mb-3">
                  Identifiant Appareil (HWID)
                </label>
                <input
                  type="text"
                  id="hwid"
                  value={hwid}
                  readOnly
                  className="w-full px-4 py-4 bg-purple-900/20 border border-purple-500/30 rounded-xl text-purple-300 cursor-not-allowed"
                />
                <p className="text-xs text-purple-400 mt-2">
                  Cet identifiant est généré automatiquement pour votre appareil
                </p>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/20 border border-red-500/50 rounded-xl p-4"
                >
                  <p className="text-red-400 text-sm">{error}</p>
                </motion.div>
              )}

              <div className="flex space-x-4">
                <motion.button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 py-4 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-300"
                >
                  Cancel
                </motion.button>
                
                <motion.button
                  type="submit"
                  disabled={isLoading || !licenseCode}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 py-4 bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-purple-500/25"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Vérification...
                    </span>
                  ) : (
                    'Ajouter la Licence'
                  )}
                </motion.button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-700">
              <div className="text-sm text-gray-500 space-y-1">
                <p>🔒 La licence sera liée à votre compte</p>
                <p>⚡ Vous pourrez basculer entre vos licences</p>
                <p>🛡️ Validation sécurisée</p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddLicenseModal;
